import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { login } from '@/store/slices/authSlice';
import { useGoogleLoginMutation, useAppleLoginMutation } from '@/store/api';
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { AUTH_ERROR_MESSAGES } from '@/constants/shared/auth';
import { isEmailValid, isPasswordValid, validateLoginForm } from '@/utils/residents/validation';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
import { UserType } from '@/types/residents/user';
import { extractErrorMessage } from '@/utils/common/errorHandling';

/**
 * Unified Auth (RES-89): the admin/resident toggle is gone. There is a single
 * credential-entry path — the resident email `login` thunk, now ungated. Its
 * response carries `availableContexts`, which `authSlice` stores; the picker /
 * `RootNavigator` then route based on the chosen context, so admins, residents,
 * and dual-context accounts all sign in through the same form.
 *
 * SSO (Google/Apple) stays "ready but dark" (the buttons remain commented out).
 * Its post-callback handlers are kept for the existing OAuth flow tests; OAuth
 * dual-mode (surfacing the picker for SSO) is a later PR, so those handlers keep
 * their current resident-side behavior.
 */
export type UseLoginFormReturn = {
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  showPassword: boolean;
  toggleShowPassword: () => void;
  isLoading: boolean;
  canSubmit: boolean;
  submit: () => Promise<void>;
  googleLogin: (credential: string) => Promise<void>;
  appleLogin: (identityToken: string, user?: { email?: string; fullName?: { givenName?: string; familyName?: string } }) => Promise<void>;
  formError: string | null;
  lastInvalidField: 'email' | 'password' | null;
};

export const useLoginForm = (): UseLoginFormReturn => {
  const dispatch = useAppDispatch();
  const { isLoading: authLoading, error: authError } = useAppSelector((state) => state.auth);

  // RTK Query hooks (SSO only — email/password goes through the `login` thunk).
  const [googleLoginMutation, { isLoading: googleLoading }] = useGoogleLoginMutation();
  const [appleLoginMutation, { isLoading: appleLoading }] = useAppleLoginMutation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [formError, setFormError] = useState<string | null>(null);
  const [lastInvalidField, setLastInvalidField] = useState<'email' | 'password' | null>(null);

  // Combined loading state
  const isLoading = authLoading || googleLoading || appleLoading;

  const validateForm = useCallback((): boolean => {
    return isEmailValid(email) && isPasswordValid(password);
  }, [email, password]);

  const canSubmit = useMemo(() => validateForm() && !isLoading, [validateForm, isLoading]);

  const toggleShowPassword = useCallback(() => setShowPassword((v) => !v), []);

  const submit = useCallback(async () => {
    // Clear any previous error
    setFormError(null);
    setLastInvalidField(null);

    // Client-side validation first
    const validationError = validateLoginForm(email, password);
    if (validationError) {
      setFormError(validationError);
      // Set focus based on which field is invalid
      if (!isEmailValid(email)) {
        setLastInvalidField('email');
      } else if (!isPasswordValid(password)) {
        setLastInvalidField('password');
      }
      return;
    }

    try {
      // Unified Auth: one login path for everyone. The slice stores the returned
      // `availableContexts`; `RootNavigator` shows the picker or routes directly.
      await dispatch(login({ email, password })).unwrap();
    } catch (err: any) {
      // Unwrap throws the rejectWithValue payload or error
      const message = typeof err === 'string' ? err : err?.message || 'Login failed';
      setFormError(message);
      // Default focus to password for re-entry
      setLastInvalidField('password');
      // Prevent unhandled promise rejection on web by resolving gracefully
      return;
    }
  }, [dispatch, email, password]);

  // Google login function using RTK Query. SSO stays resident-side for now; OAuth
  // dual-mode (picker for SSO) is a later PR.
  const googleLogin = useCallback(async (credential: string) => {
    setFormError(null);
    setLastInvalidField(null);

    try {
      const result = await googleLoginMutation({
        credential,
        isAdmin: false,
      }).unwrap();

      // Validate response structure
      if (!result?.response?.user || !result?.response?.accessToken) {
        throw new Error('Invalid login response from server');
      }

      const { user, accessToken } = result.response;

      // Resident OAuth: reject admin-only accounts (admin OAuth is a later PR).
      if (user.roles && user.roles.length > 0) {
        throw new Error('Admin accounts cannot login to the Resident Portal.');
      }

      // For resident users, validate they have a valid lease
      if (user.type === UserType.RESIDENT) {
        // Save token first (needed for lease check API call)
        await SecureStorage.getInstance().saveToken(accessToken);

        // Verify user has valid lease
        const leaseResponse = await AuthAPI.checkLease();
        if (!leaseResponse.success || !leaseResponse.response?.hasLease) {
          // Mirror regular login behavior: clear tokens on invalid lease
          try {
            await SecureStorage.getInstance().clearTokens();
          } catch (clearError) {
            console.error('Failed to clear tokens:', clearError);
          }
          throw new Error('You do not have a valid lease. Please contact your property manager.');
        }
      } else {
        // Non-resident, non-admin users (guests/guarantors)
        await SecureStorage.getInstance().saveToken(accessToken);
      }
    } catch (err: any) {
      // Use extractErrorMessage to get the most specific error message (e.g., description field)
      const message = extractErrorMessage(err, 'Google login failed');
      setFormError(message);
      console.error('❌ Google login error:', err);
      throw err; // Re-throw to allow LoginForm to handle the error
    }
  }, [googleLoginMutation]);

  // Apple login function using RTK Query. Same resident-side behavior as Google.
  const appleLogin = useCallback(async (
    identityToken: string,
    user?: { email?: string; fullName?: { givenName?: string; familyName?: string } }
  ) => {
    setFormError(null);
    setLastInvalidField(null);

    try {
      const result = await appleLoginMutation({
        identityToken,
        isAdmin: false,
        user,
      }).unwrap();

      // Validate response structure
      if (!result?.response?.user || !result?.response?.accessToken) {
        throw new Error('Invalid login response from server');
      }

      const { user: responseUser, accessToken } = result.response;

      // Resident OAuth: reject admin-only accounts (admin OAuth is a later PR).
      if (responseUser.roles && responseUser.roles.length > 0) {
        throw new Error('Admin accounts cannot login to the Resident Portal.');
      }

      // For resident users, validate they have a valid lease
      if (responseUser.type === UserType.RESIDENT) {
        // Save token first (needed for lease check API call)
        await SecureStorage.getInstance().saveToken(accessToken);

        // Verify user has valid lease
        const leaseResponse = await AuthAPI.checkLease();
        if (!leaseResponse.success || !leaseResponse.response?.hasLease) {
          // Mirror regular login behavior: clear tokens on invalid lease
          try {
            await SecureStorage.getInstance().clearTokens();
          } catch (clearError) {
            console.error('Failed to clear tokens:', clearError);
          }
          throw new Error('You do not have a valid lease. Please contact your property manager.');
        }
      } else {
        // Non-resident, non-admin users (guests/guarantors)
        await SecureStorage.getInstance().saveToken(accessToken);
      }
    } catch (err: any) {
      // Use extractErrorMessage to get the most specific error message (e.g., description field)
      const message = extractErrorMessage(err, 'Apple login failed');
      setFormError(message);
      console.error('❌ Apple login error:', err);
      throw err; // Re-throw to allow LoginForm to handle the error
    }
  }, [appleLoginMutation]);

  // Surface slice-level errors (e.g., if another component dispatched login without unwrap)
  useEffect(() => {
    if (typeof authError === 'string' && authError.length > 0) {
      if (authError === AUTH_ERROR_MESSAGES.NO_AUTH_TOKEN) {
        return;
      }
      setFormError(authError);
    }
  }, [authError]);

  return {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    toggleShowPassword,
    isLoading,
    canSubmit,
    submit,
    googleLogin,
    appleLogin,
    formError,
    lastInvalidField,
  };
};
